'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { closeNotif, startNotif } from '@/components/DynamicForm/util/notifier'
import Form, { FormGeneratorHandles } from '@/components/Form'
import Splash from '@/components/splash/Splash'
import { Button } from '@/components/UiElements'
import getSettings from '@/services/Settings/getSettings'
import upsertSetting from '@/services/Settings/upsertSetting'
import { getErrorMessage } from '@/utils/errorMessages'
import notifyError from '@/utils/notifyError'

import { customizationElementSettingsFormItems } from './formItems'
import {
  DEFAULT_CUSTOMIZATION_ELEMENT_SETTINGS_FORM_VALUES,
  mapFormValuesToSettings,
  mapSettingsToFormValues,
} from './mapper'
import { CustomizationElementSettingsFormValues, ELEMENT_SIZE_SETTING_KEY, MIN_RESOLUTION_SETTING_KEY } from './types'

type LoadState = 'loading' | 'ready' | 'error'

type CustomizationElementSettingsFormProps = {
  onCancel: () => void
  onSaved: () => void
}

const CustomizationElementSettingsForm = ({ onCancel, onSaved }: CustomizationElementSettingsFormProps) => {
  const formRef = useRef<FormGeneratorHandles>(null)
  const loadRequestId = useRef(0)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [isSaving, setIsSaving] = useState(false)

  const loadSettings = useCallback(async () => {
    const requestId = ++loadRequestId.current
    setLoadState('loading')

    try {
      const settings = await getSettings()
      if (requestId !== loadRequestId.current) return

      formRef.current?.setData(mapSettingsToFormValues(settings))
      setLoadState('ready')
    } catch (error) {
      if (requestId !== loadRequestId.current) return

      setLoadState('error')
      notifyError(error, 'Unable to load customization element settings.')
    }
  }, [])

  useEffect(() => {
    void loadSettings()

    return () => {
      loadRequestId.current += 1
    }
  }, [loadSettings])

  const handleSave = async () => {
    if (loadState !== 'ready' || isSaving) return

    const validationErrors = await formRef.current?.validate()
    if (validationErrors?.length) return

    const formData = formRef.current?.getData()
    if (!formData) return

    const formValues = formData as CustomizationElementSettingsFormValues
    const settings = mapFormValuesToSettings(formValues)
    const notificationId = startNotif('Saving customization element settings...')
    setIsSaving(true)

    try {
      await Promise.all([
        upsertSetting({
          key: ELEMENT_SIZE_SETTING_KEY,
          value: settings.elementSize,
        }),
        upsertSetting({
          key: MIN_RESOLUTION_SETTING_KEY,
          value: settings.minimumResolution,
        }),
      ])

      closeNotif(notificationId, 'Customization element settings saved successfully.')
      onSaved()
    } catch (error) {
      closeNotif(notificationId, getErrorMessage(error, 'Failed to save customization element settings.'), {
        type: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const isBusy = loadState === 'loading' || isSaving

  return (
    <div className="flex w-[720px] max-w-[calc(100vw-80px)] flex-col gap-xxxl">
      {isBusy && (
        <Splash
          text={isSaving ? 'Saving customization element settings...' : 'Loading customization element settings...'}
        />
      )}

      {loadState !== 'error' && (
        <div className="grid grid-cols-12 gap-md">
          <Form
            ref={formRef}
            items={customizationElementSettingsFormItems}
            defaultValues={DEFAULT_CUSTOMIZATION_ELEMENT_SETTINGS_FORM_VALUES}
          />
        </div>
      )}

      {loadState === 'error' && (
        <div className="flex min-h-[180px] flex-col items-center justify-center gap-md rounded-md bg-gray-50 p-xl">
          <p className="text-text-md text-gray-400">Customization element settings could not be loaded.</p>
          <Button type="alt" onClick={() => void loadSettings()}>
            Retry
          </Button>
        </div>
      )}

      <div className="flex justify-end gap-md">
        <Button size="lg" type="alt" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button size="lg" onClick={() => void handleSave()} disabled={loadState !== 'ready' || isSaving}>
          Save
        </Button>
      </div>
    </div>
  )
}

export default CustomizationElementSettingsForm
